/**
 * Bandwidth Test Engine - Parallel download speed measurement
 *
 * Uses parallel connections and continuous downloading to accurately
 * measure maximum bandwidth, similar to fast.com approach.
 */

// ============================================================================
// Types
// ============================================================================

export interface BandwidthTestConfig {
  baseUrl: string;
  parallelConnections: number;
  testDurationMs: number;
  chunkUrl: string; // URL to download (should be large file)
  onProgress?: (progress: BandwidthProgress) => void;
}

export interface BandwidthProgress {
  currentSpeedMbps: number;
  averageSpeedMbps: number;
  downloadedBytes: number;
  elapsedMs: number;
  activeConnections: number;
}

export interface LatencyResult {
  unloaded: number; // Latency before bandwidth test (ms)
  loaded: number; // Latency during bandwidth test (ms)
  jitter: number; // Standard deviation of loaded latency samples (ms)
}

export interface ChunkResult {
  bytes: number;
  durationMs: number;
  speedMbps: number;
}

export interface BandwidthTestResult {
  speedMbps: number;
  peakSpeedMbps: number;
  downloadedBytes: number;
  testDurationMs: number;
  latency: LatencyResult;
  bufferbloatRatio: number; // loaded / unloaded ratio
  connectionCount: number;
  chunks: ChunkResult[]; // Individual chunk download stats
}

// ============================================================================
// Default Configuration
// ============================================================================

const DEFAULT_CONFIG: Partial<BandwidthTestConfig> = {
  parallelConnections: 6,
  testDurationMs: 45000, // 45 seconds
  chunkUrl: 'chunk/25mb', // Using /chunk/{size}/{id} route - worker handles mapping to actual file
};

// ============================================================================
// Bandwidth Test Implementation
// ============================================================================

export class BandwidthTest {
  private config: BandwidthTestConfig;
  private abortController: AbortController | null = null;
  private isRunning = false;

  constructor(config: Partial<BandwidthTestConfig> & { baseUrl: string }) {
    this.config = { ...DEFAULT_CONFIG, ...config } as BandwidthTestConfig;
  }

  /**
   * Measure unloaded latency (ping when idle)
   */
  async measureUnloadedLatency(): Promise<number> {
    const url = `${this.config.baseUrl}/manifest.json`;
    const samples: number[] = [];

    // Take 3 samples
    for (let sampleIndex = 0; sampleIndex < 3; sampleIndex++) {
      const start = performance.now();
      try {
        await fetch(url, {
          method: 'HEAD',
          cache: 'no-store',
        });
        const latency = performance.now() - start;
        samples.push(latency);
      } catch {
        // Ignore failed samples
      }
    }

    if (samples.length === 0) {
      return 0;
    }

    // Return median
    samples.sort((first, second) => first - second);
    return samples[Math.floor(samples.length / 2)];
  }

  /**
   * Measure loaded latency (ping during heavy download)
   */
  private async measureLoadedLatency(): Promise<number> {
    const url = `${this.config.baseUrl}/manifest.json`;
    const start = performance.now();

    try {
      await fetch(url, {
        method: 'HEAD',
        cache: 'no-store',
        signal: this.abortController?.signal,
      });
      return performance.now() - start;
    } catch {
      return 0;
    }
  }

  /**
   * Run the bandwidth test with parallel downloads
   */
  async run(): Promise<BandwidthTestResult> {
    this.abortController = new AbortController();
    this.isRunning = true;
    this.chunkCounter = 0; // Reset chunk counter for stable URLs

    const { parallelConnections, testDurationMs, baseUrl, chunkUrl } =
      this.config;
    const downloadUrl = `${baseUrl}/${chunkUrl}`;

    console.log('[BandwidthTest] Starting test', {
      parallelConnections,
      testDurationMs,
      downloadUrl,
    });

    // Measure unloaded latency first
    const unloadedLatency = await this.measureUnloadedLatency();
    console.log('[BandwidthTest] Unloaded latency:', unloadedLatency, 'ms');

    const startTime = performance.now();
    let totalBytes = 0;
    let peakSpeedMbps = 0;
    const loadedLatencySamples: number[] = [];
    const chunkResults: ChunkResult[] = [];

    // Track active downloads
    const activeDownloads = new Map<
      number,
      { promise: Promise<number>; startTime: number }
    >();
    let downloadId = 0;

    // Speed calculation window (last 2 seconds)
    const speedWindow: Array<{ time: number; bytes: number }> = [];

    const calculateCurrentSpeed = (): number => {
      const now = performance.now();
      const windowStart = now - 2000; // 2 second window

      // Remove old entries
      while (speedWindow.length > 0 && speedWindow[0].time < windowStart) {
        speedWindow.shift();
      }

      if (speedWindow.length < 2) {
        return 0;
      }

      const firstEntry = speedWindow[0];
      const lastEntry = speedWindow[speedWindow.length - 1];
      const timeDiff = lastEntry.time - firstEntry.time;
      const bytesDiff = lastEntry.bytes - firstEntry.bytes;

      if (timeDiff <= 0) {
        return 0;
      }

      return (bytesDiff * 8) / (timeDiff / 1000) / 1_000_000;
    };

    // Track streaming bytes for real-time progress (total across all downloads)
    let lastProgressUpdate = performance.now();

    const handleStreamProgress = (bytes: number) => {
      totalBytes += bytes; // Add bytes as they stream in
      const now = performance.now();
      // Update progress every 200ms for smooth UI updates
      if (now - lastProgressUpdate > 200) {
        lastProgressUpdate = now;
        speedWindow.push({ time: now, bytes: totalBytes });
        const currentSpeed = calculateCurrentSpeed();
        const elapsed = now - startTime;
        const avgSpeed =
          elapsed > 0 ? (totalBytes * 8) / (elapsed / 1000) / 1_000_000 : 0;

        this.config.onProgress?.({
          currentSpeedMbps: currentSpeed,
          averageSpeedMbps: avgSpeed,
          downloadedBytes: totalBytes,
          elapsedMs: elapsed,
          activeConnections: activeDownloads.size,
        });
      }
    };

    // Start a single download
    const startDownload = (): void => {
      if (!this.isRunning) return;

      const currentId = downloadId++;
      const downloadStartTime = performance.now();

      const downloadPromise = this.downloadChunk(
        downloadUrl,
        handleStreamProgress
      );

      activeDownloads.set(currentId, {
        promise: downloadPromise,
        startTime: downloadStartTime,
      });

      downloadPromise
        .then((bytes) => {
          if (!this.isRunning) return;

          const downloadDuration = performance.now() - downloadStartTime;
          const chunkSpeedMbps =
            (bytes * 8) / (downloadDuration / 1000) / 1_000_000;

          chunkResults.push({
            bytes,
            durationMs: Math.round(downloadDuration),
            speedMbps: Math.round(chunkSpeedMbps * 10) / 10,
          });

          // Note: bytes already counted in handleStreamProgress, just update peak
          const currentSpeed = calculateCurrentSpeed();
          if (currentSpeed > peakSpeedMbps) {
            peakSpeedMbps = currentSpeed;
          }

          activeDownloads.delete(currentId);

          // Start another download if still running
          if (
            this.isRunning &&
            performance.now() - startTime < testDurationMs
          ) {
            startDownload();
          }
        })
        .catch((downloadError) => {
          activeDownloads.delete(currentId);
          // AbortError is expected when test ends - don't log or retry
          if (
            downloadError instanceof Error &&
            downloadError.name === 'AbortError'
          ) {
            return;
          }
          console.error('[BandwidthTest] Download failed:', downloadError);
          // Retry if still running
          if (
            this.isRunning &&
            performance.now() - startTime < testDurationMs
          ) {
            startDownload();
          }
        });
    };

    // Start initial parallel downloads
    for (
      let connectionIndex = 0;
      connectionIndex < parallelConnections;
      connectionIndex++
    ) {
      startDownload();
    }

    // Measure loaded latency periodically during the test
    const latencyInterval = setInterval(async () => {
      if (!this.isRunning) return;
      const latency = await this.measureLoadedLatency();
      if (latency > 0) {
        loadedLatencySamples.push(latency);
      }
    }, 1000);

    // Wait for test duration
    await new Promise<void>((resolve) => {
      const checkInterval = setInterval(() => {
        const elapsed = performance.now() - startTime;
        if (elapsed >= testDurationMs || !this.isRunning) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });

    // Cleanup
    this.isRunning = false;
    clearInterval(latencyInterval);
    this.abortController.abort();

    // Wait for active downloads to abort
    await Promise.allSettled(
      Array.from(activeDownloads.values()).map((d) => d.promise)
    );

    const totalDuration = performance.now() - startTime;
    const avgSpeedMbps =
      totalDuration > 0
        ? (totalBytes * 8) / (totalDuration / 1000) / 1_000_000
        : 0;

    console.log('[BandwidthTest] Test complete', {
      totalBytes: `${(totalBytes / 1_000_000).toFixed(1)}MB`,
      totalDuration: `${(totalDuration / 1000).toFixed(2)}s`,
      avgSpeedMbps: `${avgSpeedMbps.toFixed(1)} Mbps`,
      peakSpeedMbps: `${peakSpeedMbps.toFixed(1)} Mbps`,
      loadedLatencySamples,
    });

    // Calculate median loaded latency and jitter
    let loadedLatency = 0;
    let jitter = 0;

    if (loadedLatencySamples.length > 0) {
      // Sort for median calculation
      const sortedSamples = [...loadedLatencySamples].sort(
        (first, second) => first - second
      );
      loadedLatency = sortedSamples[Math.floor(sortedSamples.length / 2)];

      // Calculate jitter (standard deviation)
      if (loadedLatencySamples.length > 1) {
        const mean =
          loadedLatencySamples.reduce((sum, val) => sum + val, 0) /
          loadedLatencySamples.length;
        const squaredDiffs = loadedLatencySamples.map(
          (val) => (val - mean) ** 2
        );
        const avgSquaredDiff =
          squaredDiffs.reduce((sum, val) => sum + val, 0) / squaredDiffs.length;
        jitter = Math.sqrt(avgSquaredDiff);
      }
    }

    // Calculate bufferbloat ratio (how much latency increases under load)
    const bufferbloatRatio =
      unloadedLatency > 0 ? loadedLatency / unloadedLatency : 0;

    return {
      speedMbps: Math.round(avgSpeedMbps * 10) / 10,
      peakSpeedMbps: Math.round(peakSpeedMbps * 10) / 10,
      downloadedBytes: totalBytes,
      testDurationMs: Math.round(totalDuration),
      latency: {
        unloaded: Math.round(unloadedLatency),
        loaded: Math.round(loadedLatency),
        jitter: Math.round(jitter * 10) / 10,
      },
      bufferbloatRatio: Math.round(bufferbloatRatio * 10) / 10,
      connectionCount: parallelConnections,
      chunks: chunkResults,
    };
  }

  // Track chunk counter for stable URLs
  private chunkCounter = 0;

  /**
   * Download a chunk and return bytes received
   * @param onStreamProgress - callback for real-time byte progress
   */
  private async downloadChunk(
    url: string,
    onStreamProgress?: (bytes: number) => void
  ): Promise<number> {
    // Stable chunk URL: /chunk/25mb/001
    // Each chunk has a unique, predictable ID - no timestamps
    // Worker handles no-cache headers and maps to actual binary file
    const chunkId = String(this.chunkCounter++).padStart(3, '0');
    const fullUrl = `${url}/${chunkId}`;

    const response = await fetch(fullUrl, {
      cache: 'no-store',
      signal: this.abortController?.signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const reader = response.body.getReader();
    let totalBytes = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      totalBytes += value.length;
      onStreamProgress?.(value.length);
    }

    return totalBytes;
  }

  /**
   * Abort the running test
   */
  abort(): void {
    this.isRunning = false;
    this.abortController?.abort();
  }

  /**
   * Format speed for display
   */
  static formatSpeed(mbps: number): string {
    if (mbps >= 1000) {
      return `${(mbps / 1000).toFixed(1)} Gbps`;
    }
    return `${Math.round(mbps)} Mbps`;
  }

  /**
   * Format latency for display
   */
  static formatLatency(ms: number): string {
    return `${Math.round(ms)} ms`;
  }

  /**
   * Format bytes for display
   */
  static formatBytes(bytes: number): string {
    if (bytes >= 1_000_000_000) {
      return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
    }
    if (bytes >= 1_000_000) {
      return `${(bytes / 1_000_000).toFixed(0)} MB`;
    }
    if (bytes >= 1000) {
      return `${(bytes / 1000).toFixed(0)} KB`;
    }
    return `${bytes} B`;
  }
}
